import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, DateRangePickerComponent, DateRange } from '../../../shared/components/primeng';
import { ActionDropdownTriggerDirective } from '../../../shared/directives/action-dropdown-trigger.directive';
import { AclApdApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';

export interface AssetRequestData {
  assetName: string;
  assetType: string;
  activity: string;
  assetStatus: string;
  requestedBy: string;
  consumerEmail: string;
  organisationName: string;
  purpose: string;
  publishedOn: string;
  action: string;
  originalData?: any;
}

@Component({
  selector: 'app-asset-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    ModalComponent,
    DateRangePickerComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './asset-request.component.html',
  styleUrl: './asset-request.component.scss',
})
export class AssetRequestComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('assetRequestActionCell', { static: false }) assetRequestActionCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestStatusCell', { static: false }) assetRequestStatusCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestPublishedOnCell', { static: false }) assetRequestPublishedOnCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestExpandCell', { static: false }) assetRequestExpandCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestChildRow', { static: false }) assetRequestChildRowTemplate!: TemplateRef<any>;

  constructor(
    private aclApdApiService: AclApdApiService,
    private router: Router,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {}

  assetRequestColumns: TableColumn[] = [
    { key: 'expand', label: '', sortable: false, width: '44px', fixed: 'left' },
    { key: 'assetName', label: 'Asset Name', sortable: true },
    { key: 'assetType', label: 'Asset Type', sortable: false },
    { key: 'activity', label: 'Activity', sortable: true },
    { key: 'assetStatus', label: 'Asset Status', sortable: true },
    { key: 'publishedOn', label: 'Published On', sortable: true },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '120px' },
  ];

  assetRequestData: AssetRequestData[] = [];
  assetRequestPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  assetRequestLoading = false;
  assetRequestEmptyMessage = 'No asset requests found';

  // Sort configuration
  currentSort: { field: string; order: 'asc' | 'desc' } = { field: 'assetName', order: 'asc' };

  openDropdownId: string | null = null;
  dropdownPosition: { top: number; left: number } = { top: 0, left: 0 };
  
  // Track expanded rows for child row functionality
  expandedRows: Set<string> = new Set();

  // Approve modal state
  isApproveModalVisible = false;
  selectedRequest: AssetRequestData | null = null;
  expirationDate: DateRange | null = null;
  minDate: Date = new Date();

  ngOnInit(): void {
    this.loadAssetRequests();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.assetRequestExpandCellTemplate) {
        const assetRequestExpandColumn = this.assetRequestColumns.find(col => col.key === 'expand');
        if (assetRequestExpandColumn) {
          assetRequestExpandColumn.cellTemplate = this.assetRequestExpandCellTemplate;
        }
      }
      if (this.assetRequestActionCellTemplate) {
        const assetRequestActionColumn = this.assetRequestColumns.find(col => col.key === 'action');
        if (assetRequestActionColumn) {
          assetRequestActionColumn.cellTemplate = this.assetRequestActionCellTemplate;
        }
      }
      if (this.assetRequestStatusCellTemplate) {
        const assetRequestStatusColumn = this.assetRequestColumns.find(col => col.key === 'assetStatus');
        if (assetRequestStatusColumn) {
          assetRequestStatusColumn.cellTemplate = this.assetRequestStatusCellTemplate;
        }
      }
      if (this.assetRequestPublishedOnCellTemplate) {
        const assetRequestPublishedOnColumn = this.assetRequestColumns.find(col => col.key === 'publishedOn');
        if (assetRequestPublishedOnColumn) {
          assetRequestPublishedOnColumn.cellTemplate = this.assetRequestPublishedOnCellTemplate;
        }
      }
    }, 100);
  }

  /** Map raw asset type to display label (e.g. adex:DataBank -> Dataset). */
  getAssetTypeDisplayLabel(rawType: string): string {
    if (!rawType || typeof rawType !== 'string') return '';
    const t = rawType.toLowerCase();
    if (t.includes('databank') || t.includes('database')) return 'Dataset';
    if (t.includes('aimodel') || t.includes('model')) return 'Model';
    if (t.includes('app') || t.includes('usecase')) return 'App';
    return rawType;
  }

  /** Format Published On as DD-MMM-YYYY (e.g. 27-Jan-2026). */
  formatPublishedOn(dateValue: string): string {
    if (!dateValue) return '—';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return dateValue;
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /** Status display label: Pending, Granted, etc. */
  getStatusLabel(status: string): string {
    if (!status) return '—';
    const s = (status + '').toUpperCase();
    if (s === 'GRANTED') return 'Granted';
    if (s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') return 'Active';
    if (s === 'PENDING') return 'Pending';
    if (s === 'DRAFT') return 'Draft';
    if (s === 'REJECTED' || s === 'FAILED' || s === 'DENIED') return 'Rejected';
    return status;
  }

  /** CSS class for status chip (Pending = yellow, Granted = green). */
  getStatusChipClass(status: string): string {
    if (!status) return 'contribution-chip contribution-chip--status-default';
    const s = (status + '').toUpperCase();
    if (s === 'GRANTED' || s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') return 'contribution-chip contribution-chip--status-active';
    if (s === 'PENDING') return 'contribution-chip contribution-chip--status-pending';
    if (s === 'DRAFT') return 'contribution-chip contribution-chip--status-draft';
    if (s === 'REJECTED' || s === 'FAILED' || s === 'DENIED') return 'contribution-chip contribution-chip--status-rejected';
    return 'contribution-chip contribution-chip--status-default';
  }

  /** Convert string to Pascal Case (e.g., "DOWNLOAD" -> "Download"). */
  private toPascalCase(str: string): string {
    if (!str || typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /** Check if action buttons should be disabled (status is Granted or Rejected). */
  isActionDisabled(row: AssetRequestData): boolean {
    if (!row || !row.assetStatus) return false;
    const status = (row.assetStatus + '').toUpperCase().trim();
    return status === 'GRANTED' || status === 'REJECTED' || status === 'FAILED' || status === 'DENIED';
  }

  /** Normalize API item to AssetRequestData; handles nested structure and type formatting. */
  private mapItemToAssetRequest(item: any): AssetRequestData {
    const asset = item.asset || {};
    const user = item.user || {};
    const additionalInfo = item.additionalInfo || {};
    
    const assetName = asset.assetName || asset.asset_name || item.assetName || item.asset_name || item.name || '';
    
    const rawType = asset.assetType || asset.asset_type || item.assetType || item.asset_type || item.type || '';
    const assetTypeRaw = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
    const assetTypeStr = typeof assetTypeRaw === 'string' ? assetTypeRaw : '';
    
    const requestType = asset.requestType || item.requestType || item.activity || item.action || '';
    const activity = requestType ? this.toPascalCase(requestType) : '';
    
    const statusRaw = asset.requestStatus || item.requestStatus || item.assetStatus || item.asset_status || item.status || 'PENDING';
    
    const firstName = (user.consumerFirstName || user.consumer_first_name || user.firstName || user.first_name || '').toString().trim();
    const lastName  = (user.consumerLastName  || user.consumer_last_name  || user.lastName  || user.last_name  || '').toString().trim();
    const requestedBy = [firstName, lastName].filter(Boolean).join(' ') ||
                        item.requestedBy || item.requested_by || item.userName || item.user_name || '';

    const consumerEmail = user.consumerEmail || user.consumer_email || user.email ||
                          item.consumerEmail || item.consumer_email || item.email || '';

    const organisationName = additionalInfo.organisationName || additionalInfo.organisation_name || 
                            item.organisationName || item.organisation_name || item.orgName || item.org_name || '';
    
    const purpose = additionalInfo.purposeOfRequest || additionalInfo.purpose_of_request || 
                   item.purpose || item.reason || '';
    
    const publishedOn = asset.createdAt || asset.created_at || 
                       item.publishedOn || item.published_on || 
                       item.createdAt || item.created_at || '';
    
    return {
      assetName: assetName,
      assetType: this.getAssetTypeDisplayLabel(assetTypeStr) || assetTypeStr,
      activity: activity,
      assetStatus: statusRaw,
      requestedBy: requestedBy,
      consumerEmail: consumerEmail,
      organisationName: organisationName,
      purpose: purpose,
      publishedOn: publishedOn,
      action: '',
      originalData: item
    };
  }

  async loadAssetRequests(): Promise<void> {
    this.assetRequestLoading = true;
    
    const mockData = this.assetRequestData.filter(d => d.originalData?.id?.startsWith('mock-'));
    
    try {
      const page = this.assetRequestPagination.currentPage;
      const size = this.assetRequestPagination.pageSize;
      const sort = `${this.getApiSortField(this.currentSort.field)}:${this.currentSort.order}`;
      
      const response = await this.aclApdApiService.getAssetRequestForAnOrganisation(page, size, sort);

      console.log('Asset Request API Response:', response);

      let mappedData: AssetRequestData[] = [];
      let totalItems = 0;

      if (response) {
        if (response['result'] && Array.isArray(response['result'])) {
          console.log('Mapping result array, items count:', response['result'].length);
          mappedData = response['result'].map((item: any) => {
            console.log('Mapping item:', item);
            const mapped = this.mapItemToAssetRequest(item);
            console.log('Mapped result:', mapped);
            return mapped;
          });
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          mappedData = response['data'].map((item: any) => this.mapItemToAssetRequest(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (Array.isArray(response)) {
          mappedData = response.map((item: any) => this.mapItemToAssetRequest(item));
          totalItems = mappedData.length;
        }
      }

      if (mappedData.length === 0 && mockData.length > 0) {
        this.assetRequestData = mockData;
        this.assetRequestPagination = { 
          ...this.assetRequestPagination, 
          totalItems: mockData.length 
        };
      } else {
        this.assetRequestData = mappedData;
        this.assetRequestPagination = { 
          ...this.assetRequestPagination, 
          totalItems: totalItems 
        };
      }
    } catch (error) {
      console.error('Error loading asset requests:', error);
      if (mockData.length > 0) {
        this.assetRequestData = mockData;
        this.assetRequestPagination = { ...this.assetRequestPagination, totalItems: mockData.length };
      } else {
        this.assetRequestData = [];
        this.assetRequestPagination = { ...this.assetRequestPagination, totalItems: 0 };
      }
    } finally {
      this.assetRequestLoading = false;
    }
  }

  onAssetRequestPageChange(page: number): void {
    this.assetRequestPagination = { ...this.assetRequestPagination, currentPage: page };
    this.loadAssetRequests();
  }

  onAssetRequestPageSizeChange(pageSize: number): void {
    this.assetRequestPagination = { ...this.assetRequestPagination, pageSize: pageSize, currentPage: 1 };
    this.loadAssetRequests();
  }

  onAssetRequestSortChange(sort: { column: string; direction: 'asc' | 'desc' }): void {
    this.currentSort = { field: sort.column, order: sort.direction };
    this.assetRequestPagination = { ...this.assetRequestPagination, currentPage: 1 };
    this.loadAssetRequests();
  }

  private getApiSortField(field: string): string {
    const fieldMapping: { [key: string]: string } = {
      assetName: 'assetName',
      activity: 'requestType',
      assetStatus: 'requestStatus',
      publishedOn: 'createdAt'
    };
    return fieldMapping[field] || field;
  }

  toggleActionDropdown(event: Event, row: AssetRequestData): void {
    event.stopPropagation();
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);
    
    if (this.openDropdownId === rowId) {
      this.openDropdownId = null;
    } else {
      const button = event.currentTarget as HTMLElement;
      const rect = button.getBoundingClientRect();
      
      this.dropdownPosition = {
        top: rect.bottom + 4,
        left: rect.right - 160
      };
      
      this.openDropdownId = rowId;
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(row: AssetRequestData): boolean {
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  /** Index in assetRequestData of the row whose action dropdown is open; null when none. */
  get openAssetRequestDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) return null;
    const idx = this.assetRequestData.findIndex(
      (r) => (r?.originalData?.requestId ?? r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId
    );
    return idx >= 0 ? idx : null;
  }

  /**
   * Toggle row expansion for child rows
   */
  toggleRowExpansion(row: AssetRequestData): void {
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);
    if (this.expandedRows.has(rowId)) {
      this.expandedRows.delete(rowId);
    } else {
      this.expandedRows.add(rowId);
    }
  }

  /**
   * Check if a row is expanded
   */
  isRowExpanded(row: AssetRequestData): boolean {
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);
    return this.expandedRows.has(rowId);
  }

  /**
   * Get the bound isRowExpanded function for passing to child component
   */
  getIsRowExpanded(): (row: any) => boolean {
    return (row: any) => this.isRowExpanded(row);
  }

  handleAction(action: string, row: AssetRequestData): void {
    if (this.isActionDisabled(row)) {
      return;
    }

    this.closeActionDropdown();
    console.log(`${action} action for:`, row);

    if (action === 'approve') {
      // Open approve modal to capture expiry before approving
      this.selectedRequest = row;
      this.expirationDate = null;
      setTimeout(() => {
        this.isApproveModalVisible = true;
        this.cdr.detectChanges();
      }, 0);
    } else if (action === 'reject') {
      this.rejectAssetRequest(row);
    }
  }

  private async approveAssetRequest(row: AssetRequestData): Promise<void> {
    const requestId = row.originalData?.requestId || row.originalData?.id;

    if (!requestId) {
      console.warn('Unable to approve asset request: missing request ID', row);
      this.toasterService.error('Unable to approve asset request: missing request ID');
      return;
    }

    try {
      if (!this.expirationDate?.startDate) {
        console.error('Expiry date is required to approve asset request');
        return;
      }

      const expiryAt = this.formatExpiryAt(this.expirationDate.startDate);

      const payload = {
        requestId: requestId,
        status: 'granted',
        expiryAt: expiryAt,
      };

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      
      this.toasterService.success('Asset request has been approved successfully');
      this.closeApproveModal();
      this.loadAssetRequests();
    } catch (error) {
      console.error('Error approving asset request:', error);
      this.toasterService.error('Failed to approve asset request. Please try again.');
    }
  }

  onApproveConfirm(): void {
    if (!this.selectedRequest) {
      return;
    }
    this.approveAssetRequest(this.selectedRequest);
  }

  closeApproveModal(): void {
    this.isApproveModalVisible = false;
    this.selectedRequest = null;
    this.expirationDate = null;
  }

  isExpiryValid(): boolean {
    if (!this.expirationDate?.startDate) return false;
    const d = new Date(this.expirationDate.startDate);
    d.setHours(23, 59, 59, 999);
    return d.getTime() > Date.now();
  }

  private formatExpiryAt(date: Date): string {
    const d = new Date(date);
    d.setHours(23, 59, 0, 0);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59:00`;
  }

  private async rejectAssetRequest(row: AssetRequestData): Promise<void> {
    const requestId = row.originalData?.requestId || row.originalData?.id;

    if (!requestId) {
      console.warn('Unable to reject asset request: missing request ID', row);
      this.toasterService.error('Unable to reject asset request: missing request ID');
      return;
    }

    try {
      const payload = {
        requestId: requestId,
        status: 'rejected'
      };

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      
      this.toasterService.success('Asset request has been rejected successfully');
      this.loadAssetRequests();
    } catch (error) {
      console.error('Error rejecting asset request:', error);
      this.toasterService.error('Failed to reject asset request. Please try again.');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('window:resize', ['$event'])
  onWindowScrollOrResize(): void {
    if (this.openDropdownId !== null) {
      this.closeActionDropdown();
    }
  }

  ngOnDestroy(): void {
    this.closeActionDropdown();
  }
}
